import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, _ } from '@ngx-translate/core';
import { FilterVariantComponent } from 'src/app/components/filters/forms/filter-variant/filter-variant.component';
import { ManualVariantSelection } from 'src/app/models/create-offer/filter-data';
import { Filter, FilterValue } from 'src/app/models/filter';
import { OfferVariantGroup } from 'src/app/models/offer';
import { OfferVariantPost, OfferVariantValuePost, PostOfferVariantDefinition } from 'src/app/models/offer-definition';
import { AppPlatform } from 'src/app/models/platform';

interface OfferGroupSelection {
    filter: Filter;
    selectedValues: any[]; // Raw form values - can be strings or objects { value: string }
    variantSelections: { [groupValueKey: string]: { [variantFilterCode: string]: any[] } };
}

interface VariantGenerationResult {
    variants: OfferVariantPost[];
    variantGroups: OfferVariantGroup[];
    totalCount: number;
    isValid: boolean;
    errorMessage?: string;
}

@Component({
    selector: 'app-offer-group-variant-selector',
    templateUrl: './offer-group-variant-selector.component.html',
    styleUrls: ['./offer-group-variant-selector.component.scss'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, FilterVariantComponent],
})
export class OfferGroupVariantSelectorComponent implements OnInit, OnChanges {
    @Input() variantFilters: Filter[] = [];
    @Input() allFilters: Filter[] = [];
    @Input() platform?: AppPlatform;
    /** Selection saved on Next. Applied on init and again if it arrives after the forms exist. */
    @Input() initialSelection?: ManualVariantSelection | null;
    /**
     * Variant groups already stored on filter data.
     * Used to rebuild the cards when this step is created again, without a second saved format.
     */
    @Input() initialVariantGroups?: OfferVariantGroup[] | null;
    @Output() variantsChange = new EventEmitter<VariantGenerationResult>();
    @Output() selectionChange = new EventEmitter<ManualVariantSelection>();
    private restoring = false;

    protected form: FormGroup = new FormGroup({
        offerGroupSelections: new FormGroup({}),
        variantSelections: new FormGroup({}),
    });

    protected offerGroupFilter: Filter;
    protected otherVariantFilters: Filter[] = [];
    protected currentSelection: OfferGroupSelection;
    protected expandedGroups: { [key: string]: boolean } = {};
    protected validationResult: VariantGenerationResult = {
        variants: [],
        variantGroups: [],
        totalCount: 0,
        isValid: false,
        errorMessage: undefined,
    };
    /** Stays false while typing. Suivant sets it so the banner can appear, and stay visible if a value is cleared later. */
    protected showErrors = false;

    ngOnInit() {
        this.initializeFilters();
        this.initializeForm();
        this.setupFormListeners();
        this.restoreFromInitialState();
    }

    ngOnChanges(changes: SimpleChanges) {
        // React to changes in input filters
        if (changes['variantFilters'] && !changes['variantFilters'].isFirstChange()) {
            // Preserve current selection before reinitializing
            const previousSelection = this.currentSelection ? { ...this.currentSelection } : null;

            this.initializeFilters();

            // Restore previous selection if it exists
            if (previousSelection && previousSelection.selectedValues.length > 0) {
                this.currentSelection.selectedValues = previousSelection.selectedValues;
                this.currentSelection.variantSelections = previousSelection.variantSelections;
            }

            if (!this.hasRestorableVariantGroups() && this.initialSelection?.selectedValues?.length) {
                this.restoreInitialSelection();
            } else if (this.currentSelection) {
                // Conditional filters can appear after colors are already selected.
                // Sync each open card, then recount once. Do not replay saved groups over an edit in progress.
                this.updateVariantSelectionForms();
                this.updateVariantSelections();
                this.calculateVariants();
            } else {
                this.calculateVariants();
            }
        }

        if (changes['initialVariantGroups'] && !changes['initialVariantGroups'].isFirstChange()) {
            this.restoreInitialVariantGroups();
        }

        if (changes['initialSelection'] && !changes['initialSelection'].isFirstChange() && !this.hasRestorableVariantGroups()) {
            this.restoreInitialSelection();
        }
    }

    /** Groups already on the offer win over the manual selection snapshot. */
    private restoreFromInitialState(): void {
        if (this.hasRestorableVariantGroups()) {
            this.restoreInitialVariantGroups();
            return;
        }
        this.restoreInitialSelection();
    }

    private hasRestorableVariantGroups(): boolean {
        return (this.initialVariantGroups?.length ?? 0) > 0;
    }

    /**
     * Fill offer-group and variant controls from the saved selection.
     * Group forms are created before variant values are written, and nothing
     * is emitted until that write has finished.
     */
    private restoreInitialSelection(): void {
        const selection = this.initialSelection;
        if (!selection?.selectedValues?.length || !this.offerGroupFilter || !this.currentSelection) {
            return;
        }
        this.applySelection(selection);
    }

    /**
     * Rebuild the cards from variant groups, then count them.
     * Choice values are copied onto the live filters first: a reloaded choice
     * filter has an empty value list until the user opens it, so a count made
     * before that copy stays at zero.
     */
    private restoreInitialVariantGroups(): void {
        if (!this.offerGroupFilter || !this.currentSelection) {
            return;
        }
        const selection = this.initialVariantGroups?.length ? this.selectionFromVariantGroups(this.initialVariantGroups) : null;
        if (!selection) {
            this.restoreInitialSelection();
            return;
        }
        this.applySelection(selection);
    }

    private applySelection(selection: ManualVariantSelection): void {
        const code = this.offerGroupFilter.getCode();
        const offerGroupControl = this.form.get('offerGroupSelections')?.get(code);
        if (!offerGroupControl) {
            return;
        }

        this.restoring = true;
        try {
            this.setControlValue(offerGroupControl, selection.selectedValues);
            this.updateOfferGroupSelection();
            this.updateVariantSelectionForms();

            const variantSelectionsForm = this.form.get('variantSelections') as FormGroup;
            for (const [groupKey, filters] of Object.entries(selection.variantSelections ?? {})) {
                const groupForm = variantSelectionsForm.get(groupKey) as FormGroup | null;
                if (!groupForm || !filters) {
                    continue;
                }
                for (const [filterCode, value] of Object.entries(filters)) {
                    const control = groupForm.get(filterCode);
                    if (!control) {
                        continue;
                    }
                    this.setControlValue(control, value);
                }
            }
            this.updateVariantSelections();
        } finally {
            this.restoring = false;
        }
        this.calculateVariants();
    }

    private selectionFromVariantGroups(groups: OfferVariantGroup[]): ManualVariantSelection | null {
        const selectedValues: unknown[] = [];
        const variantSelections: ManualVariantSelection['variantSelections'] = {};

        for (const group of groups) {
            const formValue = this.formValueFromVariantValue(this.offerGroupFilter, group?.value);
            if (formValue == null) {
                continue;
            }
            const key = this.getValueKey(formValue);
            if (selectedValues.some((existing) => this.getValueKey(existing) === key)) {
                continue;
            }
            selectedValues.push(formValue);

            const perFilter: { [filterCode: string]: unknown[] } = {};
            for (const variant of group.variants ?? []) {
                for (const definition of variant?.definitions ?? []) {
                    const filterId = definition?.filter ?? definition?.filterEntity?.id;
                    if (filterId == null || filterId === this.offerGroupFilter.id) {
                        continue;
                    }
                    const filter = this.otherVariantFilters.find((item) => item.id === filterId);
                    if (!filter || !definition.value) {
                        continue;
                    }
                    const cell = this.formValueFromVariantValue(filter, definition.value);
                    if (cell == null) {
                        continue;
                    }
                    const filterCode = filter.getCode();
                    const list = perFilter[filterCode] ?? [];
                    if (!list.some((existing) => this.getValueKey(existing) === this.getValueKey(cell))) {
                        list.push(cell);
                    }
                    perFilter[filterCode] = list;
                }
            }
            variantSelections[key] = perFilter;
        }

        if (selectedValues.length === 0) {
            return null;
        }
        return { selectedValues, variantSelections };
    }

    /**
     * One cell of the variant form: a choice code, or `{ value }` for a typed filter.
     * Choice rows are merged onto the filter so the following count can resolve them.
     */
    private formValueFromVariantValue(filter: Filter, value: OfferVariantValuePost | null | undefined): unknown | null {
        if (!value) {
            return null;
        }
        switch (filter.type) {
            case 'single_choice':
            case 'multi_choice': {
                const entity = value.filterValueEntity ?? filter.filterValues.find((item) => item.id === value.filterValue) ?? null;
                if (!entity?.code) {
                    return null;
                }
                FilterValue.mergeIntoFilter(filter, [entity]);
                return entity.code;
            }
            case 'integer':
            case 'year':
            case 'decimal':
            case 'boolean': {
                if (value.integerValue == null || !Number.isFinite(Number(value.integerValue))) {
                    return null;
                }
                if (filter.type === 'boolean') {
                    return { value: Number(value.integerValue) === 1 ? '1' : '0' };
                }
                return { value: String(value.integerValue) };
            }
            case 'text': {
                if (value.textValue == null || value.textValue === '') {
                    return null;
                }
                return { value: value.textValue };
            }
            case 'date': {
                if (value.textValue == null || value.textValue === '') {
                    return null;
                }
                return { value: this.dateInputValue(value.textValue) };
            }
            case 'composition':
            case 'opening_hours':
                return null;
            default: {
                const _exhaustive: never = filter.type;
                return _exhaustive;
            }
        }
    }

    /**
     * Keep a calendar date in the input when that string regenerates the same stored value.
     * Otherwise keep the stored text so the variant code does not change.
     */
    private dateInputValue(textValue: string): string {
        const parsed = new Date(textValue);
        if (Number.isNaN(parsed.getTime())) {
            return textValue;
        }
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        const iso = `${parsed.getFullYear()}-${month}-${day}`;
        if (String(new Date(iso)) === textValue) {
            return iso;
        }
        return textValue;
    }

    private setControlValue(control: AbstractControl, value: unknown): void {
        if (value == null) {
            return;
        }
        if (control instanceof FormArray && Array.isArray(value)) {
            this.resizeCustomRows(control, value.length);
        }
        control.setValue(value, { emitEvent: false });
    }

    private resizeCustomRows(array: FormArray, length: number): void {
        if (length < 1) {
            return;
        }
        const validator = (array.at(0) as FormGroup | undefined)?.get('value')?.validator ?? null;
        while (array.length < length) {
            array.push(new FormGroup({ value: new FormControl('', validator) }), { emitEvent: false });
        }
        while (array.length > length) {
            array.removeAt(array.length - 1, { emitEvent: false });
        }
    }

    private initializeFilters() {
        // Find the offer group filter
        this.offerGroupFilter = this.variantFilters.find((f) => f.canCreateOfferGroup);

        // Get other variant filters (non-offer-group)
        this.otherVariantFilters = this.variantFilters.filter((f) => !f.canCreateOfferGroup);

        if (!this.offerGroupFilter) {
            console.error('OfferGroupVariantSelectorComponent: No offer group filter found');
            return;
        }

        // Only initialize currentSelection if it doesn't exist yet
        if (!this.currentSelection) {
            this.currentSelection = {
                filter: this.offerGroupFilter,
                selectedValues: [],
                variantSelections: {},
            };
        } else {
            // Update the filter reference but preserve the selections
            this.currentSelection.filter = this.offerGroupFilter;
        }
    }

    private initializeForm() {
        if (!this.offerGroupFilter) return;

        // Create form control for offer group filter selection
        const offerGroupControl = this.offerGroupFilter.toVariantFormControl();
        (this.form.get('offerGroupSelections') as FormGroup).addControl(this.offerGroupFilter.getCode(), offerGroupControl);

        // Initialize variant selections form group (will be populated dynamically)
        this.form.setControl('variantSelections', new FormGroup({}));
    }

    private setupFormListeners() {
        // Listen to offer group filter selection changes
        this.form.get('offerGroupSelections').valueChanges.subscribe(() => {
            if (this.restoring) {
                return;
            }
            this.updateOfferGroupSelection();
            this.updateVariantSelectionForms();
            this.updateVariantSelections();
            this.calculateVariants();
        });

        // Listen to variant selections changes
        this.form.get('variantSelections').valueChanges.subscribe(() => {
            if (this.restoring) {
                return;
            }
            this.updateVariantSelections();
            this.calculateVariants();
        });
    }

    private updateOfferGroupSelection() {
        if (!this.offerGroupFilter) {
            this.currentSelection.selectedValues = [];
            return;
        }

        const offerGroupFormValue = this.form.get('offerGroupSelections')?.get(this.offerGroupFilter.getCode())?.value;

        if (!offerGroupFormValue || !Array.isArray(offerGroupFormValue)) {
            this.currentSelection.selectedValues = [];
            return;
        }

        // Store raw form values (can be strings or objects { value: "500" })
        this.currentSelection.selectedValues = offerGroupFormValue;

        // Initialize variant selections for new groups, remove for deselected groups
        for (const value of offerGroupFormValue) {
            const key = this.getValueKey(value);
            if (!this.currentSelection.variantSelections[key]) {
                this.currentSelection.variantSelections[key] = {};
            }
        }

        // Clean up deselected groups
        const currentKeys = offerGroupFormValue.map((v) => this.getValueKey(v));
        Object.keys(this.currentSelection.variantSelections).forEach((key) => {
            if (!currentKeys.includes(key)) {
                delete this.currentSelection.variantSelections[key];
                delete this.expandedGroups[key];
            }
        });
    }

    // Helper to get a consistent key from a value (string or object)
    getValueKey(value: any): string {
        if (typeof value === 'string') {
            return value;
        } else if (value && typeof value === 'object' && value.value !== undefined) {
            return String(value.value);
        }
        return String(value);
    }

    private updateVariantSelectionForms() {
        const variantSelectionsForm = this.form.get('variantSelections') as FormGroup;

        // Store current form values before making changes
        const currentFormValues: { [groupValueCode: string]: any } = {};
        Object.keys(variantSelectionsForm.controls).forEach((key) => {
            const groupForm = variantSelectionsForm.get(key) as FormGroup;
            if (groupForm) {
                currentFormValues[key] = groupForm.value;
            }
        });

        // Determine which groups to add/remove
        const currentGroups = Object.keys(variantSelectionsForm.controls);
        const newGroups = this.currentSelection.selectedValues.map((v) => this.getValueKey(v));

        // Remove forms for deselected groups
        const groupsToRemove = currentGroups.filter((group) => !newGroups.includes(group));
        groupsToRemove.forEach((groupCode) => {
            variantSelectionsForm.removeControl(groupCode, { emitEvent: false });
            // Also clean up from currentSelection if not already done
            if (this.currentSelection.variantSelections[groupCode]) {
                delete this.currentSelection.variantSelections[groupCode];
            }
        });

        // Add or update forms for selected groups
        const visibleFilterCodes = new Set(this.otherVariantFilters.map((variantFilter) => variantFilter.getCode()));
        for (const groupValueCode of newGroups) {
            let groupForm = variantSelectionsForm.get(groupValueCode) as FormGroup;

            if (!groupForm) {
                // Create new form for this group
                groupForm = new FormGroup({});
                variantSelectionsForm.addControl(groupValueCode, groupForm, { emitEvent: false });
            }

            // Remove filters that are no longer visible, such as a conditional temperature.
            for (const filterCode of Object.keys(groupForm.controls)) {
                if (!visibleFilterCodes.has(filterCode)) {
                    groupForm.removeControl(filterCode, { emitEvent: false });
                }
            }

            // Ensure all variant filter controls exist in the group form
            for (const variantFilter of this.otherVariantFilters) {
                const filterCode = variantFilter.getCode();

                if (!groupForm.contains(filterCode)) {
                    const control = variantFilter.toVariantFormControl();
                    groupForm.addControl(filterCode, control, { emitEvent: false });
                }
            }

            // Restore previous values if they existed
            if (currentFormValues[groupValueCode]) {
                // Set values for each control that exists in both old and new forms
                Object.keys(currentFormValues[groupValueCode]).forEach((filterCode) => {
                    const control = groupForm.get(filterCode);
                    if (control) {
                        const previousValue = currentFormValues[groupValueCode][filterCode];
                        if (previousValue !== null && previousValue !== undefined) {
                            control.setValue(previousValue, { emitEvent: false });
                        }
                    }
                });
            }
        }
    }

    private updateVariantSelections() {
        const variantSelectionsFormValue = this.form.get('variantSelections')?.value || {};

        // Update variant selections using the proper keys
        for (const value of this.currentSelection.selectedValues) {
            const key = this.getValueKey(value);
            const groupFormValue = variantSelectionsFormValue[key] || {};
            this.currentSelection.variantSelections[key] = groupFormValue;
        }
    }

    private calculateVariants() {
        const result: VariantGenerationResult = {
            variants: [],
            variantGroups: [],
            totalCount: 0,
            isValid: false,
            errorMessage: undefined,
        };

        try {
            // Check if filter is properly initialized
            if (!this.offerGroupFilter) {
                result.errorMessage = _('variants.error.filter_not_initialized');
                this.validationResult = result;
                this.emitVariantResult(result);
                return;
            }

            let hasIncompleteGroup = false;

            // Generate variants for each retained offer-group value.
            // Blank or non-finite inputs are not product groups.
            for (const value of this.currentSelection.selectedValues) {
                const variantGroupValue = this.toGroupVariantValue(value);
                if (!variantGroupValue) {
                    continue;
                }

                const key = this.getValueKey(value);
                const groupVariantSelections = this.currentSelection.variantSelections[key];
                if (this.otherVariantFilters.length > 0 && !this.hasEverySubvariant(groupVariantSelections)) {
                    hasIncompleteGroup = true;
                    continue;
                }

                const groupVariants = this.generateVariantsForGroup(value, groupVariantSelections);
                if (groupVariants.length === 0) {
                    continue;
                }

                const variantGroup: OfferVariantGroup = {
                    code: this.offerGroupFilter.getCode() + '_' + key,
                    filter: this.offerGroupFilter,
                    value: variantGroupValue,
                    variants: groupVariants,
                    medias: [],
                };

                result.variantGroups.push(variantGroup);
                result.variants.push(...groupVariants);
            }

            result.totalCount = result.variants.length;

            // One incomplete group blocks the step even when other groups are complete.
            if (hasIncompleteGroup) {
                result.isValid = false;
                result.errorMessage = _('variants.error.missing_subvariants');
            } else if (result.totalCount === 0) {
                // A group with no other variant filters is valid once it has a real value.
                // That path already produced a variant above. An empty group input stays invalid.
                if (
                    this.currentSelection.selectedValues.length > 0 &&
                    this.otherVariantFilters.length === 0 &&
                    this.hasUsableGroupValue()
                ) {
                    result.isValid = true;
                } else {
                    result.isValid = false;
                }
            } else if (result.totalCount > 100) {
                result.isValid = false;
                result.errorMessage = _('variants.error.too_many');
            } else {
                result.isValid = true;
            }
        } catch {
            result.isValid = false;
            result.errorMessage = _('variants.error.generation_failed');
        }

        this.validationResult = result;
        this.emitVariantResult(result);
    }

    private emitVariantResult(result: VariantGenerationResult): void {
        if (this.restoring) {
            return;
        }
        this.variantsChange.emit(result);
        this.selectionChange.emit(this.snapshotSelection());
    }

    private snapshotSelection(): ManualVariantSelection {
        const variantSelections: ManualVariantSelection['variantSelections'] = {};
        for (const [key, group] of Object.entries(this.currentSelection?.variantSelections ?? {})) {
            variantSelections[key] = { ...(group ?? {}) };
        }
        return {
            selectedValues: [...(this.currentSelection?.selectedValues ?? [])],
            variantSelections,
        };
    }

    private generateVariantsForGroup(groupValue: unknown, variantSelections: { [key: string]: unknown } | undefined): OfferVariantPost[] {
        const offerGroupValuePost = this.toGroupVariantValue(groupValue);
        if (!offerGroupValuePost) {
            return [];
        }

        const offerGroupDefinition = new PostOfferVariantDefinition({
            filter: this.offerGroupFilter.id,
            filterEntity: this.offerGroupFilter,
            value: offerGroupValuePost,
        });

        // No sub-variants: the group value alone is one variant.
        if (this.otherVariantFilters.length === 0) {
            const variant = new OfferVariantPost();
            variant.definitions = [offerGroupDefinition];
            return [variant];
        }

        const variantGroups: { filter: Filter; values: OfferVariantValuePost[] }[] = [];
        for (const filter of this.otherVariantFilters) {
            const values = this.collectUsableVariantValues(filter, variantSelections?.[filter.getCode()]);
            // Every displayed sub-variant needs a real value. Skip the whole group otherwise,
            // so a filled sibling cannot validate the step on its own.
            if (values.length === 0) {
                return [];
            }
            variantGroups.push({ filter, values });
        }

        return this.generateCombinations(variantGroups, [offerGroupDefinition]);
    }

    private hasEverySubvariant(variantSelections: { [key: string]: unknown } | undefined): boolean {
        return this.otherVariantFilters.every(
            (filter) => this.collectUsableVariantValues(filter, variantSelections?.[filter.getCode()]).length > 0,
        );
    }

    private hasUsableGroupValue(): boolean {
        return this.currentSelection.selectedValues.some((value) => this.toGroupVariantValue(value) !== null);
    }

    private toGroupVariantValue(groupValue: unknown): OfferVariantValuePost | null {
        if (typeof groupValue === 'string') {
            if (groupValue.trim() === '') {
                return null;
            }
            const offerGroupValue = this.offerGroupFilter.filterValues.find((fv) => fv.code === groupValue);
            if (!offerGroupValue) {
                return null;
            }
            return new OfferVariantValuePost({
                filterValue: offerGroupValue.id,
                filterValueEntity: offerGroupValue,
            });
        }
        if (this.isCustomInput(groupValue)) {
            return this.toCustomVariantValue(this.offerGroupFilter, groupValue.value);
        }
        return null;
    }

    /**
     * Blank strings and non-finite numbers are not variant values.
     * `parseInt('', 10)` is `NaN`, and `typeof NaN === 'number'`, so a finite check is required.
     */
    private toCustomVariantValue(filter: Filter, rawInput: unknown): OfferVariantValuePost | null {
        if (typeof rawInput === 'string' && rawInput.trim() === '') {
            return null;
        }
        if (rawInput === undefined || rawInput === null) {
            return null;
        }
        const formatted = filter.formatValue(rawInput);
        if (typeof formatted === 'number') {
            if (!Number.isFinite(formatted)) {
                return null;
            }
            return new OfferVariantValuePost({ integerValue: formatted });
        }
        if (formatted === undefined || formatted === null) {
            return null;
        }
        if (String(formatted).trim() === '') {
            return null;
        }
        return new OfferVariantValuePost({ textValue: String(formatted) });
    }

    private collectUsableVariantValues(filter: Filter, rawValues: unknown): OfferVariantValuePost[] {
        if (!Array.isArray(rawValues)) {
            return [];
        }
        const values: OfferVariantValuePost[] = [];
        for (const rawValue of rawValues) {
            if (typeof rawValue === 'string') {
                if (rawValue.trim() === '') {
                    continue;
                }
                const filterValue = filter.filterValues.find((fv) => fv.code === rawValue);
                if (filterValue) {
                    values.push(
                        new OfferVariantValuePost({
                            filterValue: filterValue.id,
                            filterValueEntity: filterValue,
                        }),
                    );
                }
                continue;
            }
            if (this.isCustomInput(rawValue)) {
                const converted = this.toCustomVariantValue(filter, rawValue.value);
                if (converted) {
                    values.push(converted);
                }
            }
        }
        return values;
    }

    private isCustomInput(value: unknown): value is { value: unknown } {
        return !!value && typeof value === 'object' && 'value' in value && (value as { value: unknown }).value !== undefined;
    }

    private generateCombinations(
        variantGroups: { filter: Filter; values: OfferVariantValuePost[] }[],
        baseDefinitions: PostOfferVariantDefinition[] = [],
        index: number = 0,
    ): OfferVariantPost[] {
        if (index >= variantGroups.length) {
            return [];
        }

        const variants: OfferVariantPost[] = [];
        const currentGroup = variantGroups[index];

        for (const value of currentGroup.values) {
            const definition = new PostOfferVariantDefinition({
                filter: currentGroup.filter.id,
                filterEntity: currentGroup.filter,
                value: value,
            });

            const currentDefinitions = [...baseDefinitions, definition];

            if (index === variantGroups.length - 1) {
                // Last group, create final variant
                const variant = new OfferVariantPost();
                variant.definitions = currentDefinitions;
                variants.push(variant);
            } else {
                // Recurse for next group
                const subVariants = this.generateCombinations(variantGroups, currentDefinitions, index + 1);
                variants.push(...subVariants);
            }
        }

        return variants;
    }

    toggleGroupExpansion(groupValue: any) {
        const key = this.getValueKey(groupValue);
        this.expandedGroups[key] = !this.expandedGroups[key];
    }

    isGroupExpanded(groupValue: any): boolean {
        const key = this.getValueKey(groupValue);
        return !!this.expandedGroups[key];
    }

    getGroupFormControl(groupValue: any): FormGroup | null {
        const key = this.getValueKey(groupValue);
        if (!key) {
            return null;
        }
        const variantSelectionsForm = this.form.get('variantSelections');
        if (!variantSelectionsForm) {
            return null;
        }
        return variantSelectionsForm.get(key) as FormGroup;
    }

    getVariantFilterForm(groupValue: any, filterCode: string): FormControl | null {
        if (!filterCode || typeof filterCode !== 'string') {
            return null;
        }
        const groupForm = this.getGroupFormControl(groupValue);
        return groupForm ? (groupForm.get(filterCode) as FormControl) : null;
    }

    hasVariantFilterControl(groupValue: any, filterCode: string): boolean {
        if (!filterCode || typeof filterCode !== 'string') {
            return false;
        }
        const groupForm = this.getGroupFormControl(groupValue);
        return groupForm ? groupForm.contains(filterCode) : false;
    }

    getGroupLabel(groupValue: any): string {
        if (typeof groupValue === 'string') {
            // Selection filter - find the label from filterValues
            const filterValue = this.offerGroupFilter?.filterValues.find((fv) => fv.code === groupValue);
            return filterValue?.value || groupValue;
        } else if (groupValue && typeof groupValue === 'object' && groupValue.value !== undefined) {
            // Input filter - use the value directly
            return String(groupValue.value);
        }
        return String(groupValue);
    }

    getVariantCountForGroup(groupValue: any): number {
        if (!this.validationResult || !this.validationResult.variants) {
            return 0;
        }

        const valueKey = this.getValueKey(groupValue);
        // Find the variant group that matches this groupValue
        const matchingGroup = this.validationResult.variantGroups.find(
            (group) => group.code === this.offerGroupFilter.getCode() + '_' + valueKey,
        );

        return matchingGroup ? matchingGroup.variants.length : 0;
    }

    getOfferGroupSelectionsForm(): FormGroup {
        return this.form.get('offerGroupSelections') as FormGroup;
    }

    hasOfferGroupFilterControl(): boolean {
        const form = this.getOfferGroupSelectionsForm();
        return form && form.contains(this.offerGroupFilter?.getCode());
    }

    markAllAsTouched(): void {
        this.showErrors = true;
        this.form.markAllAsTouched();
    }

    getSafeGroupFormControl(groupValue: any): FormGroup {
        const formGroup = this.getGroupFormControl(groupValue);
        return formGroup || new FormGroup({});
    }
}
